/**
 * WHICH CATEGORIES ACTUALLY NEED A BACKGROUND CONVERSATION.
 *
 * CORE-E Stage B. There are eighteen canonical proficiency categories. Asking a
 * manager four questions about each one, for each technician, is seventy-two
 * fields nobody will answer honestly — and a form people rush is a form that
 * manufactures credibility, which is the one outcome Stage B exists to prevent.
 *
 * So the prompt is earned, not universal. A prior only needs justifying where
 * the shop is claiming something a newcomer could not plausibly be given:
 *
 *   asserted below the threshold  ->  the default weak prior is already the
 *                                     honest answer; ask nothing
 *   asserted at or above it       ->  ask, because THIS is the claim that will
 *                                     hold its ground against real evidence
 *   named a specialty             ->  ask, because the shop has already said
 *                                     this is what the person is for
 *
 * Nothing here derives a strength. The rubric lives in one place — the database
 * (`core_e_derive_prior_strength`) — and this module never reimplements it, so
 * the UI cannot drift from the authority. It decides WHAT TO ASK, and the server
 * decides what the answers are worth.
 */

/**
 * The asserted proficiency at which a background becomes worth asking for.
 *
 * 86 is the top of the "highly reliable" band from the approved closure: below
 * it, ordinary clean work can substantiate the claim on its own within a couple
 * of weeks, so a weak prior costs nobody anything. At or above it the shop is
 * asserting advanced capability that routine work cannot confirm, and the prior
 * will be doing real work in the arithmetic for a long time.
 */
export const BACKGROUND_DETAIL_THRESHOLD = 86;

export type BackgroundPromptReason = 'ADVANCED_CLAIM' | 'NAMED_SPECIALTY';

export interface CategoryBackgroundPrompt {
  categoryKey: string;
  /** The asserted proficiency that triggered the prompt, when one did. */
  assertedScore: number | null;
  reasons: BackgroundPromptReason[];
}

/** Is the shop claiming more than ordinary work could substantiate? */
export function isAdvancedClaim(assertedScore: number | null | undefined): boolean {
  return typeof assertedScore === 'number'
    && Number.isFinite(assertedScore)
    && assertedScore >= BACKGROUND_DETAIL_THRESHOLD;
}

/**
 * The categories worth asking about, in a stable order.
 *
 * Deliberately returns a SUBSET and never "all of them": a planner that always
 * answers everything is a questionnaire with extra steps.
 */
export function planCategoryBackgroundPrompts(
  proficiencies: Record<string, number> | null | undefined,
  specialties: string[] | null | undefined,
): CategoryBackgroundPrompt[] {
  const asserted = proficiencies ?? {};
  const named = new Set((specialties ?? []).map((s) => s.trim()).filter(Boolean));

  const keys = new Set<string>([...Object.keys(asserted), ...named]);
  const out: CategoryBackgroundPrompt[] = [];

  for (const categoryKey of [...keys].sort()) {
    const raw = asserted[categoryKey];
    const score = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
    const reasons: BackgroundPromptReason[] = [];
    if (isAdvancedClaim(score)) reasons.push('ADVANCED_CLAIM');
    if (named.has(categoryKey)) reasons.push('NAMED_SPECIALTY');
    if (reasons.length > 0) out.push({ categoryKey, assertedScore: score, reasons });
  }
  return out;
}

/**
 * The manager-facing name for a derived strength.
 *
 * Presentation only. The manager reads a tier and never a number, because a
 * number invites negotiation — and the value is not theirs to negotiate. The
 * boundaries follow the approved product targets (~4 weak, ~12 established,
 * ~30 strong veteran) and describe the SAME value the server derived; they do
 * not compute it.
 */
export type BackgroundTier =
  | 'LIMITED_BACKGROUND_SUPPORT'
  | 'ESTABLISHED_BACKGROUND'
  | 'STRONG_VETERAN_BACKGROUND';

export const BACKGROUND_TIER_LABELS: Record<BackgroundTier, string> = {
  LIMITED_BACKGROUND_SUPPORT: 'Limited background support',
  ESTABLISHED_BACKGROUND: 'Established background',
  STRONG_VETERAN_BACKGROUND: 'Strong veteran background',
};

export function backgroundTierFor(derivedStrength: number | null | undefined): BackgroundTier | null {
  if (typeof derivedStrength !== 'number' || !Number.isFinite(derivedStrength)) return null;
  if (derivedStrength >= 20) return 'STRONG_VETERAN_BACKGROUND';
  if (derivedStrength >= 9) return 'ESTABLISHED_BACKGROUND';
  return 'LIMITED_BACKGROUND_SUPPORT';
}

export function backgroundTierLabel(derivedStrength: number | null | undefined): string | null {
  const tier = backgroundTierFor(derivedStrength);
  return tier ? BACKGROUND_TIER_LABELS[tier] : null;
}

/**
 * What the facts mean in the one sentence a manager actually needs.
 *
 * "How long does my opening estimate hold?" is the honest reading of prior
 * strength, and it is the reading that stops anybody mistaking it for skill.
 */
export function describeStrengthInJobs(derivedStrength: number | null | undefined): string | null {
  if (typeof derivedStrength !== 'number' || !Number.isFinite(derivedStrength)) return null;
  const n = Math.round(derivedStrength);
  return `Your estimate carries the weight of about ${n} completed job${n === 1 ? '' : 's'} `
    + 'while OptiBay gathers real evidence.';
}
